import {useService} from '@cindedi/react-application';
import {abort} from '@cindedi/request-utilities/abort';
import {A} from '@cindedi/ui/components/A';
import {Card} from '@cindedi/ui/components/Card';
import {H2} from '@cindedi/ui/components/Heading/H2';
import {Slot} from '@cindedi/ui/components/Slot';
import {isRecord} from '@cindedi/utilities/isRecord';
import {truncate} from '@cindedi/utilities/truncate';
import type {MetaFunction, LoaderFunction} from '@remix-run/node';
import {json} from '@remix-run/node';
import {useLoaderData} from '@remix-run/react';
import {formatDate} from '~/domains/blog/utilities/formatDate';
import {formatSize} from '~/domains/file-system/utilities/formatSize';
import {PublicLayout} from '~/domains/organization/components/PublicLayout';

export interface Noticias$SlugLoaderData {
  post: Blog.PublicPostDto;
}

export const meta: MetaFunction = function Noticias$SlugMeta({data}) {
  return isRecord(data) && data.post
    ? [
        {title: `${data.post.title} | Cindedi`},
        {
          name: 'description',
          content: data.post.headline
            ? truncate(data.post.headline, 155)
            : truncate(data.post.textContent, 155),
        },
      ]
    : [{title: 'Notícias - Cindedi'}];
};

export const loader: LoaderFunction = async function Noticias$SlugLoader({context: {use}, params}) {
  const Post = use('Post');
  const {slug} = params;

  if (!slug) return abort(404);

  const post = await Post.find({where: {slug, published: true}});

  if (post == null) return abort(404);

  return json({post});
};

export default function Noticias$SlugView() {
  const data = useLoaderData<Noticias$SlugLoaderData>();
  useService('organization/titlebar').setTitle(data.post.title);

  return (
    <PublicLayout cover={data.post.cover?.url}>
      <div className="flex gap-4 max-w-full flex-col lg:flex-row">
        <div className="flex flex-col flex-1 gap-2 pt-4 max-w-full">
          {data.post.headline && <span>{data.post.headline}</span>}
          <div className="flex gap-2 text-primary-8 text-sm items-center mt-1 bg-primary-1 p-4 rounded mb-4">
            <span>{data.post.user.name}</span>•<span>{formatDate(data.post.publishedAt!)}</span>
          </div>
          <div dangerouslySetInnerHTML={{__html: data.post.content}} />
        </div>
        {data.post.files.length > 0 && (
          <div className="flex flex-col w-full lg:w-72 gap-4">
            <Card aria-label={`Arquivos anexados`} className="p-4">
              <Slot name="title">
                <H2 className="pl-4 pt-4">Anexos</H2>
              </Slot>
              <ul>
                {data.post.files.map((file) => (
                  <li key={file.publicId} className="mb-4 break-words">
                    <A target="_blank" href={file.url}>
                      <div className="flex flex-col">
                        <span className="text-primary-12">{file.name}</span>
                        <span className="text-sm text-shade-11 flex gap-2">
                          <span>{formatSize(file.size)}</span>•<span>{file.extension}</span>
                        </span>
                      </div>
                    </A>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
